import { Box, Stack, Typography } from "@mui/material";

import { colors, palette } from "../theme/tokens";

// Phase progress strip for the Onboarding Checklist (2026-09-05, Bill).
//
// Desktop: one full-width row of arrow-shaped segments, one per phase, coloured
// by state only, in tints rather than solid blocks (Bill asked for the page to
// be dialed down): done = green tint with a green number, in progress = blue
// tint with a white number on blue, upcoming = surface gray. State is by the
// phase's own count alone (2026-09-24, Bill): green only once every step in it
// is done, blue once any step is done, gray before; a phase before the one in
// hand no longer reads green just for being earlier. (Yellow stood in for
// blue for an hour that day; Bill: rough on the eyes.) Counts live in the
// phase panel headers, not here. Mobile: the same phases as a vertical
// stepper (circle, name) joined by a line.
//
// Clicking a phase scrolls to its panel and opens it; nothing is locked.

export type PhaseState = "complete" | "in_progress" | "upcoming";

export interface PhaseChevronsProps {
    phases: { name: string; state: PhaseState }[];
    onSelect: (phaseIdx: number) => void;
}

const NOTCH = 12;

export const PHASE_FILL: Record<PhaseState, { bg: string; fg: string; mark: string; markFg: string; markBorder?: string }> = {
    complete: { bg: palette.successBg, fg: palette.green, mark: palette.successBg, markFg: palette.green, markBorder: palette.green },
    in_progress: { bg: palette.infoBg, fg: palette.blueDark, mark: palette.blue, markFg: colors.white },
    upcoming: { bg: colors.lightGray, fg: colors.mediumGray, mark: colors.white, markFg: colors.mediumGray, markBorder: colors.line },
};

export function PhaseChevrons({ phases, onSelect }: PhaseChevronsProps) {
    const handleKey = (idx: number) => (e: React.KeyboardEvent) => {
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onSelect(idx);
        }
    };
    // One wrapper element: the MAP widget stacks its children with a 32px gap,
    // and with two sibling lists the hidden desktop strip counted as the first
    // child, so the phone stepper sat 32px too low (Bill, 2026-09-08).
    return (
        <Box>
            {/* Desktop strip */}
            <Stack
                direction="row"
                spacing={0.5}
                component="ol"
                aria-label="Onboarding phases"
                sx={{ display: { xs: "none", md: "flex" }, listStyle: "none", m: 0, p: 0 }}
            >
                {phases.map((phase, idx) => {
                    const fill = PHASE_FILL[phase.state];
                    const first = idx === 0;
                    const last = idx === phases.length - 1;
                    // A clipped box cannot carry a CSS border, so the outline is
                    // the wrapper: same arrow shape in `line` gray, with the
                    // tinted face inset 1px inside it (Bill, 2026-09-06: the
                    // tints alone did not stand out from the page ground).
                    const shape = `polygon(0 0, ${last ? "100%" : `calc(100% - ${NOTCH}px)`} 0, 100% 50%, ${last ? "100%" : `calc(100% - ${NOTCH}px)`} 100%, 0 100%, ${first ? "0" : `${NOTCH}px`} 50%)`;
                    return (
                        <Box
                            key={phase.name}
                            component="li"
                            role="button"
                            tabIndex={0}
                            aria-current={phase.state === "in_progress" ? "step" : undefined}
                            onClick={() => onSelect(idx)}
                            onKeyDown={handleKey(idx)}
                            sx={{
                                flex: 1,
                                minWidth: 0,
                                height: 48,
                                position: "relative",
                                bgcolor: colors.line,
                                clipPath: shape,
                                cursor: "pointer",
                                userSelect: "none",
                                transition: "filter 120ms ease-out",
                                "&:hover": { filter: "brightness(0.97)" },
                                "&:focus-visible": { outline: `2px solid ${colors.brandOrange}`, outlineOffset: -2 },
                            }}
                        >
                            <Box
                                sx={{
                                    position: "absolute",
                                    inset: "1px",
                                    clipPath: shape,
                                    pl: first ? 2 : `${NOTCH + 10}px`,
                                    pr: last ? 2 : `${NOTCH + 4}px`,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1.25,
                                    bgcolor: fill.bg,
                                    color: fill.fg,
                                }}
                            >
                                <PhaseMark idx={idx} state={phase.state} size={22} />
                                <Typography
                                    sx={{
                                        fontWeight: 600,
                                        fontSize: "0.8rem",
                                        lineHeight: 1.15,
                                        color: "inherit",
                                        overflow: "hidden",
                                        display: "-webkit-box",
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: "vertical",
                                    }}
                                >
                                    {phase.name}
                                </Typography>
                            </Box>
                        </Box>
                    );
                })}
            </Stack>

            {/* Mobile stepper */}
            <Stack
                component="ol"
                aria-label="Onboarding phases"
                spacing={0}
                sx={{ display: { xs: "flex", md: "none" }, listStyle: "none", m: 0, p: 0 }}
            >
                {phases.map((phase, idx) => {
                    const last = idx === phases.length - 1;
                    return (
                        <Box
                            key={phase.name}
                            component="li"
                            role="button"
                            tabIndex={0}
                            aria-current={phase.state === "in_progress" ? "step" : undefined}
                            onClick={() => onSelect(idx)}
                            onKeyDown={handleKey(idx)}
                            sx={{ display: "flex", gap: 1.5, cursor: "pointer", "&:focus-visible": { outline: `2px solid ${colors.brandOrange}`, outlineOffset: 2, borderRadius: "6px" } }}
                        >
                            <Stack alignItems="center" sx={{ width: 26 }}>
                                <PhaseMark idx={idx} state={phase.state} size={26} />
                                {!last && <Box sx={{ width: 2, flex: 1, minHeight: 12, bgcolor: colors.lineSoft }} />}
                            </Stack>
                            <Typography
                                sx={{
                                    fontWeight: phase.state === "in_progress" ? 600 : 500,
                                    fontSize: "0.9rem",
                                    color: PHASE_FILL[phase.state].fg,
                                    pt: "3px",
                                    pb: last ? 0 : 1.25,
                                }}
                            >
                                {phase.name}
                            </Typography>
                        </Box>
                    );
                })}
            </Stack>
        </Box>
    );
}

// The numbered circle a phase carries in the strip, the stepper and the panel
// header. The number never turns into a check (Bill, 2026-09-06: the phase
// numbers must stay readable); only the colours change: done = dark green
// number and border on the chevron's own green tint, in progress = white
// number on blue, upcoming = gray number on white with a gray border.
export function PhaseMark({ idx, state, size = 24 }: { idx: number; state: PhaseState; size?: number }) {
    const fill = PHASE_FILL[state];
    return (
        <Box
            sx={{
                width: size,
                height: size,
                borderRadius: "50%",
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 600,
                fontSize: size >= 26 ? "0.8rem" : "0.72rem",
                bgcolor: fill.mark,
                color: fill.markFg,
                border: fill.markBorder ? `1px solid ${fill.markBorder}` : "none",
            }}
        >
            {idx + 1}
        </Box>
    );
}
